import axios, { type AxiosInstance } from 'axios';
import type { SsoInterface, LogoutOptions } from './types';
import { getCookie } from '@/helpers/getCookie';
import { decodeJwtPayload } from '@/auth/session';
import { clearSentryUser } from '@/config/sentry/helpers';

class SsoAuth {
  private credentials: SsoInterface;
  private connector: AxiosInstance;

  constructor() {
    this.credentials = {
      url: import.meta.env.VITE_SSO_URL,
      realm: this.extractRealmFromToken(),
      clientId: this.extractClientFromToken(),
    };
    this.connector = this.createConnector();
  }

  public createConnector(): AxiosInstance {
    return axios.create({
      baseURL: `${this.credentials.url}/realms/${this.credentials.realm}/protocol/openid-connect/`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public isEnabled() {
    return import.meta.env.VITE_ENVIRONMENT !== 'dev';
  }

  public getToken() {
    const token = localStorage.getItem('access_token');
    return token ? `Bearer ${token}` : undefined;
  }

  private extractRealmFromToken(
    token: string | null = localStorage.getItem('access_token'),
  ): string {
    const defaultValue = import.meta.env.VITE_SSO_REALM;
    if (!token) return defaultValue;
    const iss = decodeJwtPayload(token)?.iss ?? '';
    if (!iss.startsWith(import.meta.env.VITE_SSO_URL)) return defaultValue;
    return this.extractRealmFromIss(iss) ?? defaultValue;
  }

  private extractRealmFromIss(iss: string): string | null {
    const match = iss.match(/\/realms\/([^/]+)/);
    return match ? match[1] : null;
  }

  private updateConnectorBaseURL(): void {
    this.connector.defaults.baseURL = `${this.credentials.url}/realms/${this.credentials.realm}/protocol/openid-connect/`;
  }

  public extractClientFromToken(
    token: string | null = localStorage.getItem('access_token'),
  ): string {
    const defaultValue = import.meta.env.VITE_SSO_CLIENT;
    if (!token) return defaultValue;
    return decodeJwtPayload(token)?.azp ?? defaultValue;
  }

  public getClientKey(): string | null {
    return localStorage.getItem('sso_client_key');
  }

  public setClientKeyFromCookies(): void {
    const key = getCookie('sso_key');
    if (!key) return;
    localStorage.setItem('sso_client_key', key);
  }

  public validateAndApplyRealm(token: string): void {
    this.credentials.realm = this.extractRealmFromToken(token);
    this.credentials.clientId = this.extractClientFromToken(token);
    this.updateConnectorBaseURL();
  }

  public async logout(options: LogoutOptions = {}) {
    const { auth, apolloClient, additionalCleanup } = options;

    localStorage.removeItem('sso_client_key');
    clearSentryUser();

    if (apolloClient) {
      await apolloClient.stop();
      await apolloClient.resetStore();
    }

    if (auth?.options) {
      localStorage.removeItem(auth.options.refreshTokenKey);
      localStorage.removeItem(auth.options.staySignedInKey);
      auth.logout();
      auth.options!.clientSecret = this.getClientKey();
      auth.options!.clientId = this.extractClientFromToken();
    }

    if (additionalCleanup) {
      await additionalCleanup();
    }
  }
}

const ssoAuth = new SsoAuth();

export default ssoAuth;
